import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const PeerLearningContext = createContext();

export function usePeerLearning() {
  return useContext(PeerLearningContext);
}

export function PeerLearningProvider({ children }) {
  const [studyGroups, setStudyGroups] = useState([]);
  const [tutoringSessions, setTutoringSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch study groups
      const studyGroupsQuery = query(collection(db, 'studyGroups'));
      const studyGroupsSnapshot = await getDocs(studyGroupsQuery);
      const studyGroupsData = studyGroupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudyGroups(studyGroupsData);

      // Fetch tutoring sessions
      const tutoringSessionsQuery = query(collection(db, 'tutoringSessions'));
      const tutoringSessionsSnapshot = await getDocs(tutoringSessionsQuery);
      const tutoringSessionsData = tutoringSessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTutoringSessions(tutoringSessionsData);

      setLoading(false);
    } catch (err) {
      setError('Failed to fetch data. Please try again later.');
      setLoading(false);
    }
  };

  const createStudyGroup = async (data) => {
    try {
      const docRef = await addDoc(collection(db, 'studyGroups'), {
        ...data,
        createdAt: new Date().toISOString(),
        currentMembers: 1
      });
      
      const newGroup = {
        id: docRef.id,
        ...data,
        createdAt: new Date().toISOString(),
        currentMembers: 1
      };
      
      setStudyGroups(prev => [...prev, newGroup]);
      return newGroup;
    } catch (err) {
      throw new Error('Failed to create study group. Please try again.');
    }
  };

  const createTutoringSession = async (data) => {
    try {
      const docRef = await addDoc(collection(db, 'tutoringSessions'), {
        ...data,
        createdAt: new Date().toISOString(),
        booked: false
      });
      
      const newSession = {
        id: docRef.id,
        ...data,
        createdAt: new Date().toISOString(),
        booked: false
      };
      
      setTutoringSessions(prev => [...prev, newSession]);
      return newSession;
    } catch (err) {
      throw new Error('Failed to create tutoring session. Please try again.');
    }
  };

  const joinStudyGroup = async (groupId) => {
    try {
      const groupRef = doc(db, 'studyGroups', groupId);
      const group = studyGroups.find(g => g.id === groupId);
      
      if (!group) {
        throw new Error('Study group not found.');
      }
      
      if (group.currentMembers >= group.maxMembers) {
        throw new Error('Study group is full.');
      }
      
      await updateDoc(groupRef, {
        currentMembers: group.currentMembers + 1
      });
      
      setStudyGroups(prev =>
        prev.map(g =>
          g.id === groupId
            ? { ...g, currentMembers: g.currentMembers + 1 }
            : g
        )
      );
    } catch (err) {
      throw new Error('Failed to join study group. Please try again.');
    }
  };

  const bookTutoringSession = async (sessionId) => {
    try {
      const sessionRef = doc(db, 'tutoringSessions', sessionId);
      const session = tutoringSessions.find(s => s.id === sessionId);
      
      if (!session) {
        throw new Error('Tutoring session not found.');
      }
      
      if (session.booked) {
        throw new Error('Tutoring session is already booked.');
      }
      
      await updateDoc(sessionRef, {
        booked: true
      });
      
      setTutoringSessions(prev =>
        prev.map(s =>
          s.id === sessionId
            ? { ...s, booked: true }
            : s
        )
      );
    } catch (err) {
      throw new Error('Failed to book tutoring session. Please try again.');
    }
  };

  const value = {
    studyGroups,
    tutoringSessions,
    loading,
    error,
    createStudyGroup,
    createTutoringSession,
    joinStudyGroup,
    bookTutoringSession,
    fetchData
  };

  return (
    <PeerLearningContext.Provider value={value}>
      {children}
    </PeerLearningContext.Provider>
  );
} 